import React from "react";

import { Box, LinearProgress, Typography } from "@mui/material";
import { useCardSize } from "components/Report-generator/context/CardSizeContext";
import type { CubicData, KPICardData } from "store/types";

interface Props {
  data?: KPICardData;
  loading?: boolean;
}

export const KpiCard: React.FC<Props> = ({ data, loading }) => {
  if (loading || !data) {
    return (
      <Box sx={{ p: "16px 8px 8px" }}>
        <LinearProgress sx={{ mb: "4px" }} />
      </Box>
    );
  }

  const { widthPercent } = useCardSize();

  const cubics: CubicData[] = [];

  for (let i = 1; i <= 5; i++) {
    const key = `cubic${i}` as keyof KPICardData;
    const cubic = data[key];
    if (cubic) cubics.push(cubic);
  }

  if (cubics.length === 0) {
    return (
      <Box sx={{ p: "20px 24px" }}>
        {loading && <LinearProgress sx={{ mb: "4px" }} />}
        <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>No KPI data available</Typography>
      </Box>
    );
  }

  const cols = Math.min(cubics.length, 4);
  const density = widthPercent / cols;
  const fontSize = density >= 50 ? 28 : density >= 33 ? 22 : density >= 20 ? 18 : 15;
  const padding = density >= 50 ? "20px 24px" : density >= 33 ? "16px 18px" : "12px 14px";
  const gap = density >= 33 ? "8px 20px" : "6px 12px";

  return (
    <Box sx={{ p: padding }}>
      {loading && <LinearProgress sx={{ mb: "4px" }} />}
      <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap }}>
        {cubics.map((cubic, index) => (
          <Box key={index} sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: "#496682",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                mb: "4px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {cubic.title || cubic.name}
            </Typography>
            <Typography
              sx={{
                fontSize,
                fontWeight: 700,
                color: "#071f35",
                lineHeight: 1.1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {cubic.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
